/** Section 18 - the damage formula.
 *
 * Shape (this is the classic formula, not an approximation of it):
 *
 *   DMG = BASE
 *       * (1 + I_attack + I_offense/archery + I_hate + I_jousting + I_artifact)
 *       * (1 - R_defense) * (1 - R_armorer) * (1 - R_range) * (1 - R_melee) * (1 - R_wall)
 *       * (2 if lucky, 1/2 if unlucky)
 *
 * The bonuses are ADDITIVE inside one bracket; the reductions are each their own
 * MULTIPLICATIVE factor. Getting that split wrong is the single most common way
 * a clone's numbers drift from the classic's, so it is spelled out here rather than
 * folded into a generic modifier pass.
 *
 * Rounding: the factors accumulate in a wide fixed-point accumulator and the
 * damage itself is rounded exactly once, at the end (D4).
 */

import type { CreatureDefId, ModifierSrcId } from "../ids.js";
import { asModifierSrcId } from "../ids.js";
import type { Explained, Modifier, ModifierTerm, StatKey } from "../modifiers.js";
import { resolveStat } from "../modifiers.js";
import { BP_ONE, FP_ONE, clampInt, roundHalfAway } from "../numeric.js";
import { nextRange } from "../rng/pcg32.js";
import { type ContentRegistry, must } from "../content/registry.js";
import {
  F_ALIVE, F_DEFENDING, F_NO_MELEE_PENALTY, F_SHOOTER, F_TWO_HEX,
  type BattleState, hexDistance,
} from "./state.js";
import { stacksAdjacent } from "./move.js";
import { type BattleJournal, jSetField } from "./journal.js";
import { revengeBonusFp } from "./revenge.js";
import { isFlopped, schoolPack } from "./wet.js";
import {
  STAT_DAMAGE_DEALT as S_DEALT, STAT_DAMAGE_MELEE as S_MELEE,
  STAT_DAMAGE_RANGED as S_RANGED, STAT_DAMAGE_TAKEN as S_TAKEN,
  STAT_RANGE_PENALTY_CUT as S_RANGE_CUT, STAT_SLAYER_ATTACK, STAT_SLAYER_TIER, STAT_BLIND,
} from "./stats.js";

// ------------------------------------------------------------------ stat keys
//
// Minted in `stats.ts` and re-exported here, because this file is where every
// caller expects to find the damage brackets and moving them would be churn in
// half a dozen modules for no gain.

export {
  STAT_DAMAGE_DEALT, STAT_DAMAGE_MELEE, STAT_DAMAGE_RANGED, STAT_DAMAGE_TAKEN,
  STAT_RANGE_PENALTY_CUT,
} from "./stats.js";

export const SRC_ATTACK_DIFF = asModifierSrcId("battle.attack_diff");
export const SRC_DEFENSE_DIFF = asModifierSrcId("battle.defense_diff");
export const SRC_LUCK = asModifierSrcId("battle.luck");
export const SRC_REVENGE = asModifierSrcId("battle.revenge");
export const SRC_RANGE_PENALTY = asModifierSrcId("battle.range_penalty");
export const SRC_MELEE_PENALTY = asModifierSrcId("battle.melee_penalty");
export const SRC_WALL_PENALTY = asModifierSrcId("battle.wall_penalty");
export const SRC_COVER = asModifierSrcId("battle.cover");
export const SRC_HATE = asModifierSrcId("battle.hate");
export const SRC_JOUSTING = asModifierSrcId("battle.jousting");
export const SRC_DEFEND = asModifierSrcId("battle.defend");
export const SRC_SLAYER = asModifierSrcId("battle.slayer");
export const SRC_BLIND_WAKE = asModifierSrcId("battle.blind_wake");
export const SRC_PACK = asModifierSrcId("battle.pack_bond");
export const SRC_MAGIC_STRIKE = asModifierSrcId("battle.magic_strike");

// ------------------------------------------------------------------ constants

/** +5% per point of attack over defense. */
export const ATTACK_STEP_FP = 50;
/** Capped at +300%, i.e. a 60-point difference. */
export const ATTACK_BONUS_CAP_FP = 3000;
/** -2.5% per point of defense over attack. */
export const DEFENSE_STEP_FP = 25;
/** Capped at -70%, i.e. a 28-point difference. */
export const DEFENSE_REDUCTION_CAP_FP = 700;

/**
 * The most one modifier may AMPLIFY a blow: half again.
 *
 * The `S_TAKEN` clamp used to be `[0, FP_ONE]`, which made it a SHIELD stat and
 * nothing else - Shield, Gust Ward, Armorer and petrification all write a
 * positive value here and every one of them reduces the blow, while a negative
 * value was silently clamped away. So "this stack takes MORE damage" could not
 * be said at all, which is why Vulnerability did not exist.
 *
 * Bounded rather than open because the factor is applied once per modifier and
 * they multiply: three stacked amplifiers with no floor is a stack that dies to
 * a pebble. Vulnerability is 250 (a quarter more), so this leaves room for a
 * stronger one without leaving room for a bug. Deliberately smaller than
 * `FP_ONE`, the other end of the clamp: reducing a blow to nothing is a
 * known-good state and doubling one is not.
 */
export const VULNERABILITY_CAP_FP = 500;
/** Half damage beyond this many hexes; the classic's literal ten, which on this
 *  fifteen-column field is "over half the battlefield". */
export const LONG_RANGE_HEXES = 10;
/** Defending raises defense by 30% until the stack's next turn. */
export const DEFEND_BONUS_FP = 1300;
/**
 * Hard cover: the share of a *shot* a rock or a tree beside the target takes.
 *
 * Its own multiplicative factor rather than a defence bonus, for the reason the
 * range and wall penalties are: cover does not make the creature tougher, it
 * makes the shot worse, and a defence bonus would also blunt the axe of the
 * stack standing next to it. A quarter, which is half the wall penalty - a prop
 * is not a curtain wall - and it stacks multiplicatively with that penalty on
 * the one board where both can apply.
 */
export const COVER_REDUCTION_FP = 250;

/** Individual rolls up to this stack size; the classic stops rolling per creature above it. */
export const ROLL_EXACT_MAX = 10;
/** Ten samples scaled by count up to this size; above it the mean is used. */
export const ROLL_SAMPLE_MAX = 100;
const SAMPLES = 10;

/** Wider than FP_ONE so a chain of factors does not lose a percent to rounding. */
const ACC_ONE = 1_000_000;

// -------------------------------------------------------------- stat helpers

function modsFor(mods: readonly Modifier[], stat: StatKey): readonly Modifier[] {
  const out: Modifier[] = [];
  for (const m of mods) if (m.stat === stat) out.push(m);
  return out;
}

export function effectiveAttack(s: BattleState, i: number): number {
  const r = resolveStat(s.stacks.attack[i]!, modsFor(s.stacks.mods[i]!, "attack"));
  return r.value < 0 ? 0 : r.value;
}

export function effectiveDefense(s: BattleState, i: number): number {
  const base = s.stacks.defense[i]!;
  const mods = modsFor(s.stacks.mods[i]!, "defense");
  const r = resolveStat(base, mods);
  return r.value < 0 ? 0 : r.value;
}

/** The modifier a DEFEND command installs; removed when the stack's turn comes round. */
export function defendModifier(): Modifier {
  return { source: SRC_DEFEND, stat: "defense", op: "mulFp", value: DEFEND_BONUS_FP };
}

// ------------------------------------------------------------------ base roll

/**
 * Base damage for one attack, before any modifier: the damage range rolled once
 * per creature in the stack. The classic stops rolling individually for large stacks -
 * ten samples scaled by count up to a hundred creatures, the plain mean above
 * that - and so do we, because ten thousand rolls per swing is both slow and
 * statistically indistinguishable from the mean.
 */
export function rollBaseDamage(
  s: BattleState, j: BattleJournal, slot: number, countOverride?: number,
): number {
  const st = s.stacks;
  const count = countOverride ?? st.count[slot]!;
  if (count <= 0) return 0;
  const lo = resolveStat(st.damageMin[slot]!, modsFor(st.mods[slot]!, "damageMin")).value;
  const hiRaw = resolveStat(st.damageMax[slot]!, modsFor(st.mods[slot]!, "damageMax")).value;
  const min = lo < 0 ? 0 : lo;
  const max = hiRaw < min ? min : hiRaw;

  if (count > ROLL_SAMPLE_MAX) return roundHalfAway(((min + max) * count) / 2);

  const rolls = count <= ROLL_EXACT_MAX ? count : SAMPLES;
  let sum = 0;
  let stream = s.rng["battle.damage"]!;
  for (let k = 0; k < rolls; k++) {
    const [next, v] = nextRange(stream, min, max);
    stream = next;
    sum += v;
  }
  jSetField(j, s.rng, "battle.damage", stream);
  if (rolls === count) return sum;
  return roundHalfAway((sum * count) / SAMPLES);
}

// --------------------------------------------------------------------- input

export interface DamageInput {
  readonly attacker: number;
  readonly defender: number;
  /** Base damage from `rollBaseDamage`, so the formula itself stays pure. */
  readonly baseDamage: number;
  readonly ranged: boolean;
  /** +1 lucky, -1 unlucky, 0 neither. Rolled by the caller against `battle.luck`. */
  readonly luck: -1 | 0 | 1;
  /** Hexes the attacker crossed to reach the target, for jousting. */
  readonly hexesTravelled: number;
  /** A standing wall segment lies between shooter and target. */
  readonly wallObstructed: boolean;
  /** Retaliation damage; carried for the hooks, no arithmetic of its own. */
  readonly retaliation: boolean;
  /** Flat attack/defence deltas from field perks (miasma). */
  readonly attackDelta?: number;
  readonly defenseDelta?: number;
  /** Melee defence ignore in basis points (Saltreach boarding). */
  readonly defIgnoreBp?: number;
  /** Extra base damage (Emberpit cinders). */
  readonly extraBase?: number;
  /** Halve the final blow (Greenmantle canopy). */
  readonly half?: boolean;
  /** The target is in hard cover; only ever set on a shot. */
  readonly cover?: boolean;
  /**
   * `MAGIC_ATTACK`'s whole arithmetic as one fixed-point factor: the bonus the
   * charged blow adds, already multiplied by whatever the target slides off.
   * Computed by the CALLER because the protection tables and the resistance
   * stat live in `spells.ts`, and `spells.ts` imports this file - reaching the
   * other way would close a cycle. Absent on an ordinary blow.
   */
  readonly magicFp?: number;
}

// ------------------------------------------------------------------- formula

export function computeDamage(
  s: BattleState, content: ContentRegistry, inp: DamageInput,
): Explained<number> {
  const st = s.stacks;
  const a = inp.attacker, d = inp.defender;
  const terms: ModifierTerm[] = [];

  let att = effectiveAttack(s, a) + (inp.attackDelta ?? 0);
  if (att < 0) att = 0;
  // A Reef Guard shrugs off part of whatever is swung at it. The exact mirror of the
  // Grenadier's defence pierce below, read off the DEFENDER and applied to the
  // attack rather than the defence - The expansion prices it at 30% and 60%.
  {
    let ignoreBp = 0;
    for (const ab of must(content.creatures, st.def[d]!, "creature").abilities) {
      if (ab.kind === "ATTACK_IGNORE") ignoreBp += ab.pctBp;
    }
    if (ignoreBp > 0) att = att - Math.floor((att * Math.min(ignoreBp, BP_ONE)) / BP_ONE);
  }
  // A Grenadier's shot ignores a share of the target's defence. Applied to the
  // defence that enters the attack-versus-defence bracket rather than to the
  // damage afterwards, because that is what "ignores defence" means: the
  // bracket is computed as though the armour were thinner.
  let def = effectiveDefense(s, d) + (inp.defenseDelta ?? 0);
  if (def < 0) def = 0;
  const ignoreBp = inp.defIgnoreBp ?? 0;
  if (ignoreBp > 0) def = def - Math.floor((def * Math.min(ignoreBp, BP_ONE)) / BP_ONE);
  if (inp.ranged) {
    let pierceBp = 0;
    // Read straight off the def rather than through `abilities.ts`, which
    // imports THIS file - going the other way would close a cycle.
    for (const ab of must(content.creatures, st.def[a]!, "creature").abilities) {
      if (ab.kind === "DEFENSE_PIERCE") pierceBp += ab.pctBp;
    }
    if (pierceBp > 0) {
      def = def - Math.floor((def * Math.min(pierceBp, BP_ONE)) / BP_ONE);
    }
  }
  if (schoolPack(s, a)) {
    att += 1;
    def = def - Math.floor((def * 2000) / BP_ONE);
  }

  // ---- additive bracket
  let bracketFp = FP_ONE;
  if (att > def) {
    const bonus = Math.min((att - def) * ATTACK_STEP_FP, ATTACK_BONUS_CAP_FP);
    bracketFp += bonus;
    terms.push({ source: SRC_ATTACK_DIFF, op: "add", value: bonus, applied: true });
  }

  // Revenge, into the same additive bracket the attack-versus-defence
  // difference feeds - The expansion calls it "an increase to the Damage parameter
  // before the attack", which is this bracket and not a post-hoc multiplier.
  // Max HP resolved locally: `effectiveMaxHp` lives in `apply.ts`, which
  // imports THIS file, so reaching for it would close a cycle. `modsFor` and
  // `resolveStat` are already here and do the same job.
  // Gated on the ABILITY, not on the start-count marker: every stack carries
  // the marker now (Resurrection reads it as a ceiling), and for one build the
  // presence of the note was the test, which handed Revenge to every wounded
  // stack on the field.
  const avenger = must(content.creatures, st.def[a]!, "creature")
    .abilities.some((ab) => ab.kind === "REVENGE");
  const revengeHp = resolveStat(st.maxHp[a]!, modsFor(st.mods[a]!, "hp")).value;
  const revengeFp = avenger ? revengeBonusFp(s, a, revengeHp < 1 ? 1 : revengeHp) : 0;
  if (revengeFp > 0) {
    bracketFp += revengeFp;
    terms.push({ source: SRC_REVENGE, op: "add", value: revengeFp, applied: true });
  }

  const skillKey = inp.ranged ? S_RANGED : S_MELEE;
  for (const m of st.mods[a]!) {
    if (m.stat !== skillKey && m.stat !== S_DEALT) continue;
    if (m.op !== "add") continue;
    bracketFp += m.value;
    terms.push({ source: m.source, op: "add", value: m.value, applied: true });
  }

  const attackerDef = must(content.creatures, st.def[a]!, "creature");
  const targetDefId: CreatureDefId = st.def[d]!;

  /**
   * Slayer. The classic names its quarry - great_maws, manyheads, dragons, then angels and
   * devils, then thunder_colossi - and mastery lengthens the list. Naming creatures in
   * rule code is what D9 forbids, so the list is expressed as the fact those
   * creatures share: they are the biggest things on the field, and what magic
   * cannot touch. The tier it starts biting at comes down as mastery goes up.
   */
  const slayer = resolveStat(0, modsFor(st.mods[a]!, STAT_SLAYER_ATTACK)).value;
  if (slayer > 0) {
    const tierFloor = resolveStat(9, modsFor(st.mods[a]!, STAT_SLAYER_TIER)).value;
    const target = must(content.creatures, targetDefId, "creature");
    const quarry = target.tier >= tierFloor
      || target.abilities.some((x) => x.kind === "MAGIC_IMMUNITY");
    if (quarry) {
      const bonus = Math.min(slayer * ATTACK_STEP_FP, ATTACK_BONUS_CAP_FP);
      bracketFp += bonus;
      terms.push({ source: SRC_SLAYER, op: "add", value: bonus, applied: true });
    }
  }
  /**
   * PACK_BOND: fights better in company.
   *
   * Adjacency is asked of `move.ts`, which this file may import - the arrow
   * only points the other way for `abilities.ts`, which imports THIS file. It is
   * keyed on the creature and not the side: two Fang Riders together are a
   * pack, a Fang Rider next to a Scrapper is a wolf and a scrapper.
   */
  for (const ab of attackerDef.abilities) {
    if (ab.kind !== "PACK_BOND") continue;
    let packed = false;
    for (let i = 0; i < st.n && !packed; i++) {
      if (i === a || st.def[i] !== st.def[a]) continue;
      if ((st.flags[i]! & F_ALIVE) === 0 || st.count[i]! <= 0) continue;
      packed = stacksAdjacent(st, a, i);
    }
    if (!packed) continue;
    const bonus = Math.min(ab.attack * ATTACK_STEP_FP, ATTACK_BONUS_CAP_FP);
    bracketFp += bonus;
    terms.push({ source: SRC_PACK, op: "add", value: bonus, applied: true });
  }

  for (const ab of attackerDef.abilities) {
    if (ab.kind === "HATES" && ab.creature === targetDefId) {
      const v = ab.bonusPct * 10;
      bracketFp += v;
      terms.push({ source: SRC_HATE, op: "add", value: v, applied: true });
    } else if (ab.kind === "JOUSTING" && !inp.ranged && inp.hexesTravelled > 0) {
      const v = ab.pctPerHex * 10 * inp.hexesTravelled;
      bracketFp += v;
      terms.push({ source: SRC_JOUSTING, op: "add", value: v, applied: true });
    }
  }

  let acc = Math.round((bracketFp * ACC_ONE) / FP_ONE);

  // ---- multiplicative reductions
  const applyFactor = (source: ModifierSrcId, factorFp: number): void => {
    terms.push({ source, op: "mulFp", value: factorFp, applied: true });
    acc = roundHalfAway((acc * factorFp) / FP_ONE);
  };

  if (def > att) {
    const red = Math.min((def - att) * DEFENSE_STEP_FP, DEFENSE_REDUCTION_CAP_FP);
    applyFactor(SRC_DEFENSE_DIFF, FP_ONE - red);
  }
  // Both directions; see `VULNERABILITY_CAP_FP` for why the floor is bounded.
  for (const m of st.mods[d]!) {
    if (m.stat !== S_TAKEN || m.op !== "add") continue;
    applyFactor(m.source, FP_ONE - clampInt(m.value, -VULNERABILITY_CAP_FP, FP_ONE));
  }
  // The blow that wakes a Blinded stack lands at half strength.
  for (const m of st.mods[d]!) {
    if (m.stat !== STAT_BLIND) continue;
    applyFactor(SRC_BLIND_WAKE, FP_ONE / 2);
    break;
  }

  if (inp.ranged) {
    if (hexDistance(st.hex[a]!, st.hex[d]!) > LONG_RANGE_HEXES) {
      // A bow that cancels the distance penalty is a modifier on the shooter,
      // not a flag: three of them together remove it entirely, which is exactly
      // what assembling the Deadeye Bow is for.
      const cut = clampInt(resolveStat(0, modsFor(st.mods[a]!, S_RANGE_CUT)).value, 0, FP_ONE);
      const factor = FP_ONE / 2 + roundHalfAway((FP_ONE / 2) * cut / FP_ONE);
      if (factor < FP_ONE) applyFactor(SRC_RANGE_PENALTY, factor);
    }
    if (inp.wallObstructed) applyFactor(SRC_WALL_PENALTY, FP_ONE / 2);
    if (inp.cover === true) applyFactor(SRC_COVER, FP_ONE - COVER_REDUCTION_FP);
  } else if ((st.flags[a]! & F_SHOOTER) !== 0) {
    const noMelee = (st.flags[a]! & F_NO_MELEE_PENALTY) !== 0;
    if (!noMelee || isFlopped(s, a)) applyFactor(SRC_MELEE_PENALTY, FP_ONE / 2);
  }
  // The charged blow, last of the multiplicative terms: every reduction the
  // target already had has been taken off the accumulator by now, so the one
  // this factor carries is not itself eaten by another.
  {
    const magicFp = inp.magicFp ?? FP_ONE;
    if (magicFp !== FP_ONE) applyFactor(SRC_MAGIC_STRIKE, magicFp);
  }

  // ---- luck, last and exact (the expansion: a lucky strike is exactly double)
  if (inp.luck > 0) applyFactor(SRC_LUCK, 2 * FP_ONE);
  else if (inp.luck < 0) applyFactor(SRC_LUCK, FP_ONE / 2);

  const base = inp.baseDamage + (inp.extraBase ?? 0);
  let value = roundHalfAway((base * acc) / ACC_ONE);
  if (base > 0 && value < 1) value = 1;
  if (value < 0) value = 0;
  if (inp.half === true && value > 1) {
    value = roundHalfAway(value / 2);
    if (value < 1) value = 1;
  }

  return { value, base, terms };
}

// ----------------------------------------------------------------- casualties

export interface Casualties {
  /** Creatures removed from the stack. */
  readonly killed: number;
  /** HP left on the new top creature. */
  readonly topHp: number;
  /** Damage that actually landed, i.e. capped by the stack's remaining HP. */
  readonly damage: number;
}

/** Apply `damage` to a stack's HP pool; pure, so the AI can score without mutating. */
export function applyDamageToPool(
  count: number, topHp: number, maxHp: number, damage: number,
): Casualties {
  if (damage <= 0 || count <= 0) return { killed: 0, topHp, damage: 0 };
  const pool = (count - 1) * maxHp + topHp;
  const dealt = damage > pool ? pool : damage;
  const left = pool - dealt;
  if (left <= 0) return { killed: count, topHp: 0, damage: dealt };
  const newCount = Math.ceil(left / maxHp);
  const rem = left - (newCount - 1) * maxHp;
  return { killed: count - newCount, topHp: rem, damage: dealt };
}

export const isTwoHex = (s: BattleState, i: number): boolean => (s.stacks.flags[i]! & F_TWO_HEX) !== 0;
export const isDefending = (s: BattleState, i: number): boolean => (s.stacks.flags[i]! & F_DEFENDING) !== 0;
